import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Modal, Button, Grid, Icon, Message, Form, Input, TextArea } from 'semantic-ui-react';

import Config from '../../../../constants/Config';
import selectors from '../../../../selectors';
import SectionEditor from './SectionEditor';
import FieldTypeSelector from './FieldTypeSelector';
import styles from './FormEditor.module.scss';

const FormEditor = React.memo(({ existingForm, onClose, onSave }) => {
  const accessToken = useSelector(selectors.selectAccessToken);

  // Estado del formulario
  const [formName, setFormName] = useState(existingForm?.name || '');
  const [formDescription, setFormDescription] = useState(existingForm?.description || '');
  const [aiAnalysisPrompt, setAiAnalysisPrompt] = useState(existingForm?.aiAnalysisPrompt || '');
  const [sections, setSections] = useState(existingForm?.formSchema?.sections || []);

  const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
  const [showFieldTypeSelector, setShowFieldTypeSelector] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(null);

  // Ref para hacer scroll a sección nueva
  const sectionsListRef = useRef(null);
  const [scrollToIndex, setScrollToIndex] = useState(null);
  const fileInputRef = useRef(null);

  // Integrations
  const [activeIntegrations, setActiveIntegrations] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const loadIntegrations = async () => {
      try {
        const resp = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/active`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: 'include',
        });
        if (resp.ok) {
          const data = await resp.json();
          if (isMounted) {
            setActiveIntegrations(data.items || []);
          }
        }
      } catch (e) {
        // ignore
      }
    };
    if (accessToken) {
      loadIntegrations();
    }
    return () => {
      isMounted = false;
    };
  }, [accessToken]);

  // Agregar nueva sección
  const handleAddSection = () => {
    const newSection = {
      id: `section_${Date.now()}`,
      title: `Section ${sections.length + 1}`,
      order: sections.length + 1,
      fields: [],
    };

    const newSections = [...sections, newSection];
    setSections(newSections);

    // Seleccionar automáticamente la nueva sección (expandir)
    const newIndex = newSections.length - 1;
    setSelectedSectionIndex(newIndex);

    // Trigger scroll después de que React actualice el DOM
    setScrollToIndex(newIndex);
  };

  // Effect para hacer scroll a sección cuando se selecciona
  useEffect(() => {
    if (scrollToIndex !== null && sectionsListRef.current) {
      // Esperar a que el DOM se actualice
      setTimeout(() => {
        const sectionElements = sectionsListRef.current.querySelectorAll('[data-section-index]');
        const targetElement = sectionElements[scrollToIndex];

        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }

        setScrollToIndex(null);
      }, 100);
    }
  }, [scrollToIndex]);

  // Eliminar sección
  const handleDeleteSection = index => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      const newSections = sections.filter((_, i) => i !== index);
      setSections(newSections);
      if (selectedSectionIndex === index) {
        setSelectedSectionIndex(null);
      }
    }
  };

  // Actualizar sección
  const handleUpdateSection = (index, updatedSection) => {
    const newSections = [...sections];
    newSections[index] = updatedSection;
    setSections(newSections);
  };

  // Agregar campo a sección
  const handleAddField = (sectionIndex, fieldType) => {
    const newField = {
      id: `field_${Date.now()}`,
      name: `field_${Date.now()}`,
      label: '',
      type: fieldType,
      required: false,
      order: sections[sectionIndex].fields.length + 1,
    };

    // Metadatos para integraciones (Wheelbase)
    if (fieldType === 'wheelbase_vehicle') {
      newField.integration = { type: 'wheelbase', source: 'vehicle' };
      newField.placeholder = 'Search by name or VIN';
    } else if (fieldType === 'wheelbase_booking') {
      newField.integration = { type: 'wheelbase', source: 'booking' };
      newField.placeholder = 'Search by booking ID or renter';
    } else if (fieldType === 'variable') {
      // Default variable binding
      newField.variableKey = 'card.name';
      newField.variableLabel = 'Card Title';
      newField.readOnly = true;
      newField.placeholder = 'Prefilled from card';
    }

    // Agregar opciones por defecto para select/radio/checkbox
    if (['select', 'radio', 'checkbox'].includes(fieldType)) {
      newField.options = ['Option 1', 'Option 2', 'Option 3'];
    }

    const newSections = [...sections];
    newSections[sectionIndex].fields.push(newField);
    setSections(newSections);
    setShowFieldTypeSelector(false);
  };

  // Handle drag & drop de secciones
  const handleDragEnd = result => {
    if (!result.destination) return;

    const { source, destination } = result;

    // Si es drag de secciones
    if (result.type === 'section') {
      const newSections = Array.from(sections);
      const [movedSection] = newSections.splice(source.index, 1);
      newSections.splice(destination.index, 0, movedSection);

      // Actualizar orders
      newSections.forEach((section, idx) => {
        section.order = idx + 1;
      });

      setSections(newSections);
    }

    // Si es drag de campos dentro de una sección
    else if (result.type.startsWith('field-')) {
      const sectionIndex = parseInt(result.type.replace('field-', ''));
      const newSections = [...sections];
      const newFields = Array.from(newSections[sectionIndex].fields);
      const [movedField] = newFields.splice(source.index, 1);
      newFields.splice(destination.index, 0, movedField);

      // Actualizar orders
      newFields.forEach((field, idx) => {
        field.order = idx + 1;
      });

      newSections[sectionIndex].fields = newFields;
      setSections(newSections);
    }
  };

  // Guardar formulario
  const handleSave = async () => {
    if (!formName.trim()) {
      setError('Form name is required');
      return;
    }

    if (sections.length === 0) {
      setError('Add at least one section to the form');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const formSchema = {
        title: formName,
        description: formDescription,
        sections: sections,
      };

      const endpoint = existingForm
        ? `/api/inspection-forms/${existingForm.id}`
        : '/api/inspection-forms';

      const method = existingForm ? 'PATCH' : 'POST';

      const response = await fetch(`${Config.SERVER_BASE_URL}${endpoint}`, {
        method,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: formName,
          description: formDescription,
          formType: 'custom',
          formSchema: formSchema,
          templateName: 'custom',
          aiAnalysisPrompt: aiAnalysisPrompt,
        }),
      });

      if (response.ok) {
        onSave();
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to save form');
      }
    } catch (err) {
      setError('Error saving form');
      console.error('Error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClickImport = () => {
    setImportError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleImportFileChange = async e => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    try {
      setIsImporting(true);
      setImportError(null);
      const text = await file.text();

      // Pre-clean large/base64 content before sending
      let rawObj = null;
      try {
        rawObj = JSON.parse(text);
      } catch (_) {
        rawObj = null;
      }

      const isLargeString = str => typeof str === 'string' && str.length > 2000;
      const isDataUrl = str =>
        typeof str === 'string' && /^data:(?:image|application)\/[a-zA-Z0-9.+-]+;base64,/.test(str);
      const isSuspiciousKey = key => {
        const k = String(key || '').toLowerCase();
        return (
          k.includes('image') ||
          k.includes('photo') ||
          k.includes('signature') ||
          k.includes('file') ||
          k.includes('dataurl') ||
          k.includes('base64') ||
          k.includes('blob')
        );
      };

      const preservedListKeys = new Set([
        'options',
        'choices',
        'values',
        'items',
        'radioOptions',
        'selectOptions',
        'dropdownOptions',
      ]);

      const cleanValue = (val, key, parentKey) => {
        if (val == null) return val;
        if (typeof val === 'string') {
          // Nunca eliminar strings bajo keys de opciones, salvo que sean data URLs
          if (isDataUrl(val)) {
            return undefined;
          }
          if (!preservedListKeys.has(parentKey) && (isLargeString(val) || isSuspiciousKey(key))) {
            return undefined;
          }
          return val;
        }
        if (Array.isArray(val)) {
          const cleaned = val
            .map(item => cleanValue(item, key, parentKey || key))
            .filter(v => v !== undefined);
          return cleaned;
        }
        if (typeof val === 'object') {
          const out = {};
          Object.keys(val).forEach(k => {
            const v = val[k];
            // No eliminar estructuras de opciones; limpiaremos internamente sus valores
            if (
              !preservedListKeys.has(k) &&
              isSuspiciousKey(k) &&
              (typeof v === 'string' || Array.isArray(v) || typeof v === 'object')
            ) {
              // Drop heavy/sensitive keys entirely
              return;
            }
            const cleaned = cleanValue(v, k, key);
            if (cleaned !== undefined) {
              out[k] = cleaned;
            }
          });
          return out;
        }
        return val;
      };

      const payloadRawJson = rawObj ? JSON.stringify(cleanValue(rawObj)) : text;

      // Build a lightweight schema hint to minimize payload (preserve options/types/labels)
      const toSnake = str =>
        String(str || '')
          .toLowerCase()
          .normalize('NFKD')
          .replace(/[^\w\s-]/g, '')
          .trim()
          .replace(/\s+/g, '_');

      const pickStr = (obj, ...keys) => {
        for (const k of keys) {
          if (obj && typeof obj[k] === 'string' && obj[k].trim()) return obj[k].trim();
        }
        return '';
      };

      const extractOptions = f => {
        const candidates =
          f?.options ||
          f?.choices ||
          f?.values ||
          f?.items ||
          f?.radioOptions ||
          f?.selectOptions ||
          f?.dropdownOptions;
        if (!Array.isArray(candidates)) return undefined;
        return candidates
          .map(o => (typeof o === 'object' ? o?.label ?? o?.value ?? '' : String(o)))
          .filter(Boolean)
          .map(s => (s.length > 200 ? s.slice(0, 200) : s)); // trim long items
      };

      const mapType = f => {
        const t = (f?.type || f?.fieldType || '').toString().toLowerCase();
        if (['text', 'string', 'short_text', 'input'].includes(t)) return 'text';
        if (['number', 'numeric', 'float', 'integer'].includes(t)) return 'number';
        if (['textarea', 'long_text', 'multiline'].includes(t)) return 'textarea';
        if (['select', 'dropdown', 'combo'].includes(t)) return 'select';
        if (['radio', 'radiogroup'].includes(t)) return 'radio';
        if (['checkbox', 'checkboxes'].includes(t)) return 'checkbox';
        if (['date', 'datepicker'].includes(t)) return 'date';
        if (['time', 'timepicker'].includes(t)) return 'time';
        if (['signature', 'sign'].includes(t)) return 'signature';
        if (['file', 'upload'].includes(t)) return 'file';
        if (['camera', 'photo', 'picture', 'image'].includes(t)) return 'camera';
        return undefined;
      };

      const buildSchemaHint = obj => {
        try {
          const title = pickStr(obj, 'title', 'name') || 'Imported Form';
          const description = pickStr(obj, 'description', 'desc');

          let sectionsRaw = [];
          if (Array.isArray(obj?.sections)) sectionsRaw = obj.sections;
          else if (Array.isArray(obj?.pages)) sectionsRaw = obj.pages;
          else if (Array.isArray(obj?.groups)) sectionsRaw = obj.groups;
          else if (Array.isArray(obj?.questions) || Array.isArray(obj?.fields)) {
            sectionsRaw = [{ title, fields: obj.questions || obj.fields }];
          } else {
            sectionsRaw = [{ title, fields: [] }];
          }

          const sections = sectionsRaw.map((sec, si) => {
            const sTitle = pickStr(sec, 'title', 'name') || `Section ${si + 1}`;
            const fields = Array.isArray(sec?.fields)
              ? sec.fields
              : Array.isArray(sec?.questions)
              ? sec.questions
              : [];
            const mappedFields = fields.map((f, fi) => {
              const label = pickStr(f, 'label', 'title', 'name') || `Field ${fi + 1}`;
              const name = toSnake(f?.name || label || `field_${fi + 1}`);
              const t = mapType(f);
              const required = Boolean(f?.required || f?.mandatory || f?.is_required);
              const options = extractOptions(f);
              const light = { name, label, required };
              if (t) light.type = t;
              if (options && options.length > 0) light.options = options;
              return light;
            });
            return { title: sTitle, fields: mappedFields };
          });

          return JSON.stringify({
            title,
            description,
            sections,
          });
        } catch (_) {
          return payloadRawJson;
        }
      };

      const minimalRawJson = rawObj ? buildSchemaHint(rawObj) : payloadRawJson;

      // Debug sizes
      try {
        // eslint-disable-next-line no-console
        console.log('Import JSON sizes', {
          fileBytes: file.size,
          rawTextLength: text.length,
          cleanedLength: payloadRawJson.length,
          minimalLength: minimalRawJson.length,
        });
      } catch (_) {}

      const resp = await fetch(`${Config.SERVER_BASE_URL}/api/ai/convert-form-json`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          rawJson: minimalRawJson,
          preferredFormType: 'custom',
        }),
      });

      if (!resp.ok) {
        try {
          // eslint-disable-next-line no-console
          console.error('Import JSON failed', resp.status, resp.statusText);
        } catch (_) {}
        let errMsg = 'Failed to import JSON';
        try {
          const err = await resp.json();
          errMsg = err.message || errMsg;
        } catch (_) {}
        throw new Error(errMsg);
      }

      const data = await resp.json();
      if (data.name) {
        setFormName(data.name);
      }
      if (typeof data.description === 'string') {
        setFormDescription(data.description);
      }
      if (data.formSchema?.sections && Array.isArray(data.formSchema.sections)) {
        setSections(data.formSchema.sections);
      } else if (data.sections && Array.isArray(data.sections)) {
        // fallback por si el helper devuelve sections a primer nivel
        setSections(data.sections);
      } else {
        throw new Error('Invalid schema returned from converter');
      }
    } catch (e1) {
      setImportError(e1.message || 'Error importing JSON');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Modal open onClose={onClose} size="fullscreen" className={styles.editorModal}>
      <Modal.Header>
        <Icon name="edit outline" />
        {existingForm ? 'Edit Form' : 'Create New Form'}
      </Modal.Header>

      <Modal.Content className={styles.editorContent}>
        <Grid className={styles.editorGrid}>
          {/* Panel izquierdo - Editor */}
          <Grid.Column width={10} className={styles.leftPanel}>
            {error && (
              <Message negative>
                <Message.Header>Error</Message.Header>
                <p>{error}</p>
              </Message>
            )}
            {importError && (
              <Message negative>
                <Message.Header>Import Error</Message.Header>
                <p>{importError}</p>
              </Message>
            )}

            {/* Form básico info */}
            <Form className={styles.formInfo}>
              <Form.Field>
                <label>Form Name *</label>
                <Input
                  placeholder="e.g., Pre-Trip RV Inspection"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                />
              </Form.Field>

              <Form.Field>
                <label>Description</label>
                <Input
                  placeholder="Brief description of this form"
                  value={formDescription}
                  onChange={e => setFormDescription(e.target.value)}
                />
              </Form.Field>

              <Form.Field>
                <label>AI Analysis Rules (Optional)</label>
                <TextArea
                  placeholder="Define custom rules for AI analysis (e.g. 'Be strict about tire pressure', 'Ignore cosmetic scratches'). Leave empty to use default system behavior."
                  value={aiAnalysisPrompt}
                  onChange={e => setAiAnalysisPrompt(e.target.value)}
                  rows={4}
                  style={{ minHeight: '100px' }}
                />
              </Form.Field>

              {!existingForm && (
                <Form.Field>
                  <Button
                    type="button"
                    basic
                    icon
                    labelPosition="left"
                    onClick={handleClickImport}
                    loading={isImporting}
                    disabled={isImporting}
                  >
                    <Icon name="upload" />
                    Import JSON
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/json"
                    style={{ display: 'none' }}
                    onChange={handleImportFileChange}
                  />
                </Form.Field>
              )}
            </Form>

            {/* Secciones */}
            <div className={styles.sectionsContainer}>
              <div className={styles.sectionHeader}>
                <h3>Sections</h3>
                <Button
                  primary
                  size="small"
                  icon
                  labelPosition="left"
                  onClick={handleAddSection}
                  disabled={isImporting}
                >
                  <Icon name="plus" />
                  Add Section
                </Button>
              </div>

              {sections.length === 0 ? (
                <Message info>
                  <Message.Header>No sections yet</Message.Header>
                  <p>Click "Add Section" to start building your form</p>
                </Message>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="sections" type="section">
                    {provided => (
                      <div
                        className={styles.sectionsList}
                        ref={el => {
                          provided.innerRef(el);
                          sectionsListRef.current = el;
                        }}
                        {...provided.droppableProps}
                      >
                        {sections.map((section, index) => (
                          <Draggable key={section.id} draggableId={section.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                data-section-index={index}
                              >
                                <SectionEditor
                                  section={section}
                                  index={index}
                                  isSelected={selectedSectionIndex === index}
                                  isDragging={snapshot.isDragging}
                                  dragHandleProps={provided.dragHandleProps}
                                  onSelect={() => setSelectedSectionIndex(index)}
                                  onUpdate={updated => handleUpdateSection(index, updated)}
                                  onDelete={() => handleDeleteSection(index)}
                                  onAddField={() => {
                                    setSelectedSectionIndex(index);
                                    setShowFieldTypeSelector(true);
                                  }}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          </Grid.Column>

          {/* Panel derecho - Preview */}
          <Grid.Column width={6} className={styles.rightPanel}>
            <div className={styles.previewContainer}>
              <h3>
                <Icon name="eye" />
                Preview
              </h3>
              <div className={styles.preview}>
                <h2>{formName || 'Form Name'}</h2>
                <p>{formDescription || 'Form description'}</p>

                {sections.length === 0 ? (
                  <Message>
                    <p>Add sections to see preview</p>
                  </Message>
                ) : (
                  sections.map((section, idx) => (
                    <div key={section.id} className={styles.previewSection}>
                      <h4>
                        {idx + 1}. {section.title}
                      </h4>
                      {section.fields.length === 0 ? (
                        <p className={styles.emptySection}>No fields in this section</p>
                      ) : (
                        section.fields.map(field => (
                          <div key={field.id} className={styles.previewField}>
                            <label>
                              {field.label}
                              {field.required && <span className={styles.required}>*</span>}
                            </label>
                            <div className={styles.fieldPreview}>
                              <em>{field.type}</em>
                              {field.options && <small> ({field.options.length} options)</small>}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </Grid.Column>
        </Grid>
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          primary
          onClick={handleSave}
          loading={isSaving}
          disabled={isSaving || !formName.trim() || sections.length === 0}
        >
          <Icon name="save" />
          {existingForm ? 'Update Form' : 'Create Form'}
        </Button>
      </Modal.Actions>

      {/* Modal para seleccionar tipo de campo */}
      {showFieldTypeSelector && selectedSectionIndex !== null && (
        <FieldTypeSelector
          activeIntegrations={activeIntegrations}
          onSelect={fieldType => handleAddField(selectedSectionIndex, fieldType)}
          onClose={() => setShowFieldTypeSelector(false)}
        />
      )}
    </Modal>
  );
});

FormEditor.propTypes = {
  existingForm: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

FormEditor.defaultProps = {
  existingForm: null,
};

export default FormEditor;
